import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { QueryKeys } from '../QueryKeys';
import { getNotifications, INotification, NotificationType } from '../../api/getNotifications/getNotifications';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { IStatus } from '../../api/getProductLibrarySyncStatus';
import { useUserTracking } from '../useUserTracking/useUserTracking';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { FEATURE_FLAGS } from 'src/constants';
import { useAuthWrapper } from '@weareopenr/authkit';

const CONSECUTIVE_FAILURE_LIMIT = 3;

export const useNotifications = (): UseQueryResult<INotification[]> => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { captureException } = useUserTracking();
  const brandId = getBrandId();
  const { user, featureFlags } = useAuthWrapper();
  const enhancedScheduling = featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING);

  const consecutiveFailuresRef = useRef(0);
  const hasNotifiedFailureRef = useRef(false);
  const syncStatusQueryKey = [brandId, QueryKeys.librarySyncStatus];

  useEffect(() => {
    consecutiveFailuresRef.current = 0;
    hasNotifiedFailureRef.current = false;
  }, [brandId]);

  return useQuery<INotification[]>({
    queryKey: [QueryKeys.notifications, brandId],
    queryFn: async () => await getNotifications(),
    select: (data) => {
      const scheduleFiltered = enhancedScheduling
        ? data
        : data.filter((notification) => notification.type !== NotificationType.VALIDATION);

      const isAssure65Tenant =
        queryClient.getQueryData<IStatus>(syncStatusQueryKey)?.external_system_name === 'Assure65';

      return scheduleFiltered.filter(
        (notification) => notification.type !== NotificationType.ALLERGEN_VALIDATION || isAssure65Tenant,
      );
    },
    enabled: !!brandId && !!user,
    refetchInterval: 5 * 1000,
    retry: 2,
    // No sync-status invalidation here. `onSuccess` runs once per mounted observer, and several
    // hooks read this query, so each poll fired one invalidation per observer. `invalidateQueries`
    // cancels the in-flight fetch and starts another, and the status request ignores the abort
    // signal, so every one of them stayed on the wire. `useProductLibraryStatus` polls itself
    // while a sync is PENDING, which is all this was buying.
    onSuccess: () => {
      consecutiveFailuresRef.current = 0;
      hasNotifiedFailureRef.current = false;
    },
    onError: (err) => {
      captureException(err, { tags: { action: 'fetch_notifications' } });
      consecutiveFailuresRef.current += 1;

      // A failed background poll should not alarm the user: only surface an error when the
      // initial load has never succeeded (no data to show) and failures are persistent.
      const hasLoadedData = queryClient.getQueryData<INotification[]>([QueryKeys.notifications, brandId]) !== undefined;

      const shouldNotify =
        !hasLoadedData && !hasNotifiedFailureRef.current && consecutiveFailuresRef.current >= CONSECUTIVE_FAILURE_LIMIT;

      if (shouldNotify) {
        hasNotifiedFailureRef.current = true;
        showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to fetch notifications' });
      }
    },
  });
};
